import { Link } from '@tanstack/react-router';
import { budapestToday, usePhoneStats } from '../data/phone-stats';
import { Icon } from './Icon';

/**
 * A telefonos csatorna MAI szamai - a csatorna oldalaban.
 *
 * Negy szam, amibol barki egy pillantassal latja, hogy all a nap: hany hivas
 * jott, hanyat nem vettunk fel, mekkora a fogadasi arany - es hany hivo var
 * meg visszahivasra. Ez az utolso az egyetlen, amibol TEENDO lesz, ezert az
 * all kiemelve, ha nem nulla.
 *
 * Barmelyik kartyara kattintva a reszletes statisztika nyilik, a mai napra.
 */
export function PhoneKpis({
  channelId,
  variant,
}: {
  channelId: string;
  variant: 'side' | 'strip';
}) {
  const today = budapestToday();
  const yesterday = budapestToday(-1);
  const base = { users: [], direction: 'all' as const };

  const { data } = usePhoneStats(channelId, { ...base, from: today, to: today }, { live: true });
  const { data: before } = usePhoneStats(channelId, { ...base, from: yesterday, to: yesterday });

  // A kartyak LINKEK, nem gombok: igy uj lapon is megnyithatok, es a
  // kepernyoolvaso is navigaciokent jelzi oket.
  const statsSearch = { from: today, to: today };

  const totals = data?.totals;
  const rate = totals?.answerRate ?? null;
  const rateBefore = before?.totals.answerRate ?? null;
  const delta = rate !== null && rateBefore !== null ? rate - rateBefore : null;
  const percent = (value: number | null) => (value === null ? '—' : `${Math.round(value * 100)}%`);

  const cards = [
    {
      label: 'Mai hívások',
      value: totals ? String(totals.calls) : '…',
      hint: totals ? `${totals.inbound} bejövő · ${totals.outbound} kimenő` : '',
    },
    {
      label: 'Nem fogadott',
      value: totals ? String(totals.missed + totals.busy) : '…',
      hint: totals?.busy ? `ebből ${totals.busy} foglalt vonal miatt` : '',
      warn: Boolean(totals && totals.missed + totals.busy > 0),
    },
    {
      label: 'Fogadási arány',
      value: percent(rate),
      hint:
        delta === null ? (
          ''
        ) : (
          <>
            <span aria-hidden>{delta >= 0 ? '▲' : '▼'}</span>
            <span className="sr-only">
              {delta > 0 ? 'Nőtt' : delta < 0 ? 'Csökkent' : 'Nem változott'}:
            </span>{' '}
            {Math.abs(Math.round(delta * 100))} pont tegnaphoz képest
          </>
        ),
    },
    {
      label: 'Visszahívásra vár',
      value: totals ? String(totals.unreturned) : '…',
      hint: totals?.unreturned ? 'nem fogadott, még nem hívtuk vissza' : 'mindenkit visszahívtunk',
      alert: Boolean(totals?.unreturned),
    },
  ];

  if (variant === 'strip') {
    return (
      <Link
        to="/c/$channelId/statisztika"
        viewTransition
        params={{ channelId }}
        search={statsSearch}
        className="flex w-full items-center gap-3 border-b border-border-soft px-4 py-1.5 text-left text-xs text-ink-muted hover:bg-surface-hover sm:px-6"
      >
        <Icon name="telefon" />
        <span>
          Ma: <b className="text-ink">{totals?.calls ?? '…'}</b> hívás ·{' '}
          <b
            className={
              totals && totals.missed + totals.busy > 0 ? 'text-[var(--danger)]' : 'text-ink'
            }
          >
            {totals ? totals.missed + totals.busy : '…'}
          </b>{' '}
          nem fogadott · {percent(rate)} fogadva
          {totals?.unreturned ? (
            <>
              {' '}
              · <b className="text-[var(--danger)]">{totals.unreturned}</b> visszahívásra vár
            </>
          ) : null}
        </span>
        <span className="ml-auto font-medium text-accent">Statisztika →</span>
      </Link>
    );
  }

  return (
    <aside className="hidden w-60 shrink-0 flex-col gap-2.5 overflow-y-auto border-r border-border-soft px-3 py-4 xl:flex">
      <p className="px-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint">Ma</p>
      {cards.map((card) => (
        <Link
          key={card.label}
          to="/c/$channelId/statisztika"
          viewTransition
          params={{ channelId }}
          search={statsSearch}
          className={[
            'block rounded-card border px-3.5 py-3 text-left transition-[border-color,box-shadow,scale] duration-(--duration-quick)',
            'hover:shadow-(--shadow-lift) active:scale-[0.98]',
            card.alert
              ? 'border-[var(--danger)] bg-[var(--danger-soft)]'
              : 'border-border-soft bg-surface-raised hover:border-border-strong',
          ].join(' ')}
        >
          <span className="block text-xs text-ink-muted">{card.label}</span>
          <span
            className={`block font-display text-3xl font-semibold tabular-nums ${
              card.warn ? 'text-[var(--danger)]' : 'text-ink'
            }`}
          >
            {card.value}
          </span>
          {card.hint && <span className="mt-0.5 block text-2xs text-ink-faint">{card.hint}</span>}
        </Link>
      ))}
      <Link
        to="/c/$channelId/statisztika"
        viewTransition
        params={{ channelId }}
        search={statsSearch}
        className="mt-1 rounded-control px-1 py-1 text-left text-xs font-semibold text-accent hover:underline"
      >
        Részletes statisztika →
      </Link>
    </aside>
  );
}
